// src/pages/prayers/prayerDetails/prayerDetails.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input, Modal, Spin, Tag } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { getCurrentUser } from "aws-amplify/auth";
import useNotification from "../../../hooks/useNotification";
import useFileViewer from "../../../hooks/useFileViewer";
import Layout from "../../../components/layout/layout";
import "./prayerDetails.css";

const { TextArea } = Input;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  showModal: false,
  messageText: "",
  isSending: false,
  prayer: null,
  comments: [],
  notification: {
    show: false,
    message: "",
    type: "success"
  }
};

// Reducer for state management
const prayerReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setPrayer":
      return { ...state, prayer: action.payload };
    case "setComments":
      return { ...state, comments: action.payload };
    case "addComment":
      return { ...state, comments: [...state.comments, action.payload] };
    case "setShowModal":
      return { ...state, showModal: action.payload };
    case "setMessageText":
      return { ...state, messageText: action.payload };
    case "setIsSending":
      return { ...state, isSending: action.payload };
    case "setNotification":
      return { ...state, notification: action.payload };
    case "resetMessageModal":
      return { ...state, showModal: false, messageText: "" };
    default:
      return state;
  }
};

function PrayerDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(prayerReducer, initialState);
  const { isLoading, showModal, messageText, isSending, prayer, comments } = state;
  const { handleViewFile: hookHandleViewFile, loadingFiles } = useFileViewer(prayer?.user_id);

  // Format date helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true, // or false if you want 24-hour format
    });
  }, []);
  
  // FIX: Fetch comments/communication history for this prayer
  const fetchComments = useCallback(async () => {
    if (!id) return;
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${API_ENDPOINTS.PRAYER_REQUESTS_ADMIN}/${id}`,   // GET comments by prayer id
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("API RESPONSE:", response.data);
      // Normalise: accept array at root or nested under a key
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.comments)
          ? data.comments
          : [];
      dispatch({ type: "setComments", payload: list });
    } catch (error) {
      console.error("Error fetching comments:", error);
      // Non-fatal — page still works without history
    }
  }, [id]);

  // Handle send message
  const handleSendMessage = useCallback(async () => {
  	if (isSending) {
      console.warn("Message is already being sent. Skipping this call.");
      return; // 🚨 prevents duplicate calls
    }

    if (!messageText.trim()) {
      showNotification("Please enter a message", "error");
      return;
    }

    dispatch({ type: "setIsSending", payload: true });

    try {
      const token = await getAuthToken();
      const currentUser = await getCurrentUser();
      const payload = {
        prayer_id: id,
        admin_id: currentUser.userId,
        message: messageText,
      };

      const response = await axios.post(
        API_ENDPOINTS.PRAYER_ADMIN_COMMENTS,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Message sent successfully:", response.data);

      // Update local comments state with the new message
      await fetchComments();

      showNotification("Message sent successfully!", "success");
      dispatch({ type: "resetMessageModal" });
    } catch (error) {
      console.error("Error sending message:", error);
      showNotification(error.response?.data?.message || "Failed to send message.", "error");
    } finally {
      dispatch({ type: "setIsSending", payload: false });
    }
  }, [messageText, id, showNotification, isSending, fetchComments]);

  // Handle modal cancel
  const handleModalCancel = useCallback(() => {
    dispatch({ type: "resetMessageModal" });
  }, []);

  // Handle modal open
  const handleOpenModal = useCallback(() => {
    dispatch({ type: "setShowModal", payload: true });
  }, []);

  // Handle message text change
  const handleMessageTextChange = useCallback((e) => {
    dispatch({ type: "setMessageText", payload: e.target.value });
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate("/prayerList");
  }, [navigate]);

  const viewAttachment = useCallback(
    async (fileUrl, fileKey) => {
      if (!fileUrl) return;

      // Try viewing through signed URL first (same approach as other pages).
      let fileNameOnly = fileUrl;
      try {
        const parsed = new URL(String(fileUrl));
        fileNameOnly = decodeURIComponent(parsed.pathname.split("/").pop() || fileUrl);
      } catch {
        const parts = String(fileUrl).split("/");
        fileNameOnly = parts[parts.length - 1] || fileUrl;
      }

      const err = await hookHandleViewFile(fileNameOnly, fileKey, "prayer_request");
      if (err) {
        // Fallback to opening the raw URL if it is already publicly reachable
        if (typeof fileUrl === "string" && fileUrl.startsWith("http")) {
          window.open(fileUrl, "_blank", "noopener,noreferrer");
          return;
        }
        showNotification(err, "error", "File Viewer");
      }
    },
    [hookHandleViewFile, showNotification]
  );

  // Fetch prayer details
  useEffect(() => {
    let isMounted = true;

    const fetchPrayerDetails = async () => {
      if (!id) return;

      dispatch({ type: "setLoading", payload: true });

      try {
        console.log("Fetching prayer request for id:", id);
        const token = await getAuthToken();

        const response = await axios.get(`${API_ENDPOINTS.PRAYER_REQUESTS_ADMIN}/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          console.log("Prayer details response:", response.data);
          dispatch({ type: "setPrayer", payload: response.data });

          // Set form values
          form.setFieldsValue({
            title: response.data.title || "",
            description: response.data.description || "",
            submittedBy: response.data.name || "Unknown User",
            submittedOn: formatDate(response.data.created_at),
            visibility: response.data.is_public === "true" ? "Public" : "Private",
            status: response.data.status || "Submitted"
          });
        }
      } catch (error) {
        console.error("Error fetching prayer details:", error);
        if (isMounted) {
          showNotification(error.response?.data?.message || "Failed to load prayer details", "error");
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchPrayerDetails();
    fetchComments();

    return () => {
      isMounted = false;
    };
  }, [id, form, formatDate, showNotification, fetchComments]);

  // Memoize modal title
  const modalTitle = useMemo(() => `Send Message to ${prayer?.name || "User"}`, [prayer?.name]);

  // Memoize file items for rendering
  const fileItems = useMemo(() => {
    if (!prayer?.files || prayer.files.length === 0) return [];
    return prayer.files.map((file, index) => ({
      ...file,
      key: `file-${index}`,
      isImage: file.url?.match(/\.(jpg|jpeg|png|gif)$/i)
    }));
  }, [prayer?.files]);

  return (
    <Layout
      navTitle="Prayer Requests"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vpd-form-container">
        <div className="vpd-form-wrapper">
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <Spin size="large" style={{ marginBottom: "10px" }} />
              <p>Loading prayer details...</p>
            </div>
          ) : !prayer ? (
            <div style={{ textAlign: "center", padding: "32px" }}>Prayer request not found.</div>
          ) : (
            <Form form={form} layout="vertical" className="vpd-antd-form">
              <div className="vpd-form-section">
                <h3 className="vpd-section-title">Prayer Request Details</h3>
                <Form.Item label="Title" name="title">
                  <Input disabled />
                </Form.Item>
                <Form.Item label="Description" name="description">
                  <TextArea rows={4} disabled />
                </Form.Item>
              </div>

              {fileItems.length > 0 && (
                <div className="vpd-form-section">
                  <h3 className="vpd-section-title">Attached Files</h3>
                  <div className="vpd-files-section">
                    <div className="vpd-file-list">
                      {fileItems.map((file) => (
                        <div key={file.key} className="vpd-file-item">
                          <label className="vpd-file-label">{file.name || `File ${file.key}`}:</label>
                          {file.url ? (
                            <div className="vpd-file-content">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px"
                                }}
                              >
                                <span className="vpd-file-name">
                                  📄 {file.name || "Unnamed file"}
                                  {file.size_kb && ` (${file.size_kb} KB)`}
                                </span>
                                <Button className="vpd-file-item-btn" onClick={() => viewAttachment(file.url, file.key)} loading={loadingFiles[file.key]}>
                                  {loadingFiles[file.key] ? "Loading..." : "View File"}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <span className="mbd-no-document">No file uploaded.</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {prayer.audio_url && (
                <div className="vpd-form-section">
                  <h3 className="vpd-section-title">Audio Recording</h3>
                  <audio controls className="vpd-audio-player" style={{ width: "100%" }}>
                    <source src={prayer.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              <div className="vpd-form-section">
                <h3 className="vpd-section-title">Submission Information</h3>
                <div className="vpd-form-row">
                  <Form.Item label="Submitted By" name="submittedBy">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Submitted On" name="submittedOn">
                    <Input disabled />
                  </Form.Item>
                </div>
                <div className="vpd-form-row">
                  <Form.Item label="Visibility" name="visibility">
                    <Tag className="vpd-prayer-tags" color={prayer.is_public === "true" ? "green" : "red"}>
                      {prayer.is_public === "true" ? "Public" : "Private"}
                    </Tag>
                  </Form.Item>
                  <Form.Item label="Status" name="status">
                    <Tag className="vpd-prayer-tags" color="blue">
                      {prayer.status || "Submitted"}
                    </Tag>
                  </Form.Item>
                </div>
              </div>
              {/* FIX: Communication history section — renders all past messages */}
              <div className="vpd-form-section">
                <h3 className="vpd-section-title">Communication History</h3>
                {comments.length === 0 ? (
                  <p style={{ color: "#888", fontStyle: "italic" }}>
                    No messages have been sent yet.
                  </p>
                ) : (
                  <div className="vpd-comments-list">
                    {comments.map((comment, index) => (
                      <div key={comment.comment_id || index} className="vpd-comment-item">
                        <div className="vpd-comment-header">
                          <span className="vpd-comment-sender">
                            {comment.admin_name || "Admin"}
                          </span>
                          <span className="vpd-comment-date" style={{ marginLeft: "12px" }}>
                            {formatDate(comment.utc_timestamp)}
                          </span>
                        </div>

                        <p className="vpd-comment-message">
                          {comment.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="vpd-form-btn-group">
                <Button className="vpd-form-cancel-btn" onClick={handleBack}>
                  Cancel
                </Button>
                <Button className="vpd-form-message-btn" onClick={handleOpenModal}>
                  Send Message
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>

      <Modal
        className="vpd-message-modal"
        title={modalTitle}
        open={showModal}
        onOk={handleSendMessage}
        onCancel={handleModalCancel}
        okText="Send Message"
        cancelText="Cancel"
        width={600}
        confirmLoading={isSending}
      >
        <TextArea rows={6} placeholder="Type your message here..." value={messageText} onChange={handleMessageTextChange} />
      </Modal>
    </Layout>
  );
}

export default PrayerDetails;
